import * as React from 'react';
import { clsx } from 'clsx';
import { UIHeading1, UIHeading2, UIHeading3, UIHeading4 } from '@cian/ui-kit';

import { useTableScroll } from './hooks/useTableScroll';
import { HeaderRow } from './parts/HeaderRow';
import { ScrollButtons } from './parts/ScrollButtons';
import * as style from './style.css';
import { trackAddNewBuildingCompareEvent } from './tracking';

export interface INewbuildingsCompareBlockRow {
  kind: 'block';
  key: string;
  title: string;
}

export interface INewbuildingsCompareDataRow {
  kind: 'data';
  key: string;
  title?: string | null;
  cells: React.ReactNode[];
  alignTitleTop?: boolean;
  // Идущие подряд строки с одинаковым groupId образуют группу с общим hover (напр. застройщик + продолжение).
  groupId?: string;
}

export interface INewbuildingsCompareActionRow {
  kind: 'action';
  key: string;
  content: React.ReactNode;
}

export type TNewbuildingsCompareViewRow =
  | INewbuildingsCompareBlockRow
  | INewbuildingsCompareDataRow
  | INewbuildingsCompareActionRow;

export interface INewbuildingsComparePageProps {
  title: string;
  headers: React.ReactNode[];
  floatingHeaders: React.ReactNode[];
  rows: TNewbuildingsCompareViewRow[];
}

export const NewbuildingsComparePage = ({ title, headers, floatingHeaders, rows }: INewbuildingsComparePageProps) => {
  const {
    scrollRef,
    floatingHeaderScrollRef,
    headerRowRef,
    dragging,
    isFloatingHeaderMounted,
    isFloatingHeaderVisible,
    scrollByColumn,
    scrollerProps,
  } = useTableScroll();

  const handleClick = React.useCallback(() => {
    trackAddNewBuildingCompareEvent();
  }, []);

  const columnStyle = React.useMemo(() => {
    return { '--columns': floatingHeaders.length } as React.CSSProperties;
  }, [floatingHeaders.length]);

  // В первой (sticky) ячейке плавающей шапки показываем тот же заголовок, что и вверху страницы.
  const floatingHeadersWithTitle = React.useMemo(() => {
    const [, ...cells] = floatingHeaders;

    return [
      <div className={style['floating-title']} key="floating-title">
        <UIHeading3>{title}</UIHeading3>
      </div>,
      ...cells,
    ];
  }, [floatingHeaders, title]);

  const renderDataRow = (row: INewbuildingsCompareDataRow, groupPosition?: 'start' | 'end') => (
    <div
      className={clsx(
        style['row'],
        groupPosition === 'start' && style['row--group-start'],
        groupPosition === 'end' && style['row--group-end'],
      )}
      key={row.key}
      role="row"
    >
      <div className={clsx(style['cell'], row.alignTitleTop && style['cell-title-top'])} role="cell">
        <UIHeading4>{row.title}</UIHeading4>
      </div>
      {row.cells.map((cell, cellIndex) => (
        <div className={style['cell']} key={cellIndex} role="cell">
          {cell}
        </div>
      ))}
    </div>
  );

  const renderRows = () => {
    const result: React.ReactNode[] = [];

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];

      if (row.kind === 'block') {
        result.push(
          <div className={style['row']} key={row.key} role="row">
            <div className={style['block-cell']} role="cell">
              <UIHeading2>{row.title}</UIHeading2>
            </div>
          </div>,
        );
        continue;
      }

      if (row.kind === 'action') {
        result.push(
          <div className={style['row']} key={row.key} role="row">
            <div className={style['cell']} role="cell">
              {row.content}
            </div>
          </div>,
        );
        continue;
      }

      if (row.groupId) {
        // Собираем подряд идущие data-строки одной группы и оборачиваем их в общий контейнер с hover.
        const group: INewbuildingsCompareDataRow[] = [row];
        while (i + 1 < rows.length) {
          const next = rows[i + 1];
          if (next.kind !== 'data' || next.groupId !== row.groupId) {
            break;
          }
          group.push(next);
          i += 1;
        }

        result.push(
          <div className={style['row-group']} key={row.groupId}>
            {group.map((groupRow, groupIndex) =>
              renderDataRow(groupRow, groupIndex === 0 ? 'start' : groupIndex === group.length - 1 ? 'end' : undefined),
            )}
          </div>,
        );
        continue;
      }

      result.push(renderDataRow(row));
    }

    return result;
  };

  return (
    <div className={style['container']} data-testid="newbuildings-compare_page" style={columnStyle}>
      <div className={style['title']}>
        <UIHeading1 as="h1">{title}</UIHeading1>
        <ScrollButtons onScroll={scrollByColumn} />
      </div>
      {isFloatingHeaderMounted && (
        <div
          className={isFloatingHeaderVisible ? style['floating-header-visible'] : style['floating-header']}
          data-testid="FloatingHeader"
        >
          <div ref={floatingHeaderScrollRef} className={style['floating-header-scroller']}>
            <div className={style['table']} role="table">
              <HeaderRow floating headers={floatingHeadersWithTitle} onClick={handleClick} />
            </div>
          </div>
          <ScrollButtons floating onScroll={scrollByColumn} />
        </div>
      )}
      <div
        ref={scrollRef}
        className={dragging ? style['table-scroller-dragging'] : style['table-scroller']}
        data-testid="newbuildings-compare_table"
        role="region"
        tabIndex={0}
        {...scrollerProps}
      >
        <div className={style['table']} role="table">
          <HeaderRow headers={headers} rowRef={headerRowRef} onClick={handleClick} />
          {renderRows()}
        </div>
      </div>
    </div>
  );
};
